/* ---------------------------------------------------------------------------
   Reading the modeller's schedule, and solving it into calendar days.

   ══ WHY THIS IS A COPY AND NOT AN IMPORT ══

   The vendored modeller has a solver already: `Recompute()` in
   vendor/bridge-modeler/src/schedule/Schedule.ts. Nothing in `src/` is
   typechecked against `vendor/`, and the one other place this has come up
   says so in as many words: src/data/defects.ts carries the defect vocabulary
   "copied rather than imported", with a check in `npm test` that scans the
   vendored file and fails when the two diverge.

   This follows that, and since LC-43 and LC-44 it also goes further than the
   vendored solver does. The passes are in ./cpm.ts, in WORKING-DAY offsets on
   the contract's calendar (./calendar.ts), with all four link types; the
   vendored one counts every day as a working day and drives on FS alone. On a
   calendar where every day works and a programme of FS links, the two agree
   exactly, which `PG-02` checks; `PG-01` checks that the vendored model still
   stores what this reads — the four link types and a lag in working days — and
   that a working calendar is honoured.

   ══ AND WHY THE SOLVE HAPPENS HERE, ON THE CLIENT ══

   `functions/lib/modeler.ts` states the rule: nothing on the server parses a
   model. A baseline is therefore solved in the browser at the moment somebody
   freezes it, and what reaches the server is a list of calendar days, working
   durations and the links, all of which it understands. The dates are never
   re-derived, which is what "frozen" means — see migrations/0032_baselines.sql.
   The forecast (./forecast.ts) re-solves from the stored logic and what has
   happened since; it moves, and the baseline it is compared with does not.

   ══ WHAT THIS DOES NOT TOUCH ══

   The modeller's schedule stays the planning tool. Nothing here writes to it,
   and taking a baseline changes nothing in the model: it records what the plan
   said on a day.
   --------------------------------------------------------------------------- */

import type { BaselineActivity, BaselineLink } from './variance.ts';
import { CalendarIndex, EVERY_DAY, type WorkCalendar } from './calendar.ts';
import { isLinkType, solveCpm, type CpmLink, type CpmNode } from './cpm.ts';
import { isIsoDay } from './periods.ts';

/**
 * The schedule as `Schedule.Serialize()` writes it into the model state.
 *
 * Every field optional and every read defended, because this is the one place
 * in the register that reads a shape owned by a vendored folder. A drop that
 * renamed a field must produce an empty programme and a sentence saying so,
 * not an exception in the middle of a save.
 */
export interface SerialisedSchedule {
  projectStartIso?: string;
  activities?: {
    id?: string;
    name?: string;
    parentId?: string | null;
    children?: string[];
    durationDays?: number;
    manualStartDays?: number | null;
    componentKind?: string;
  }[];
  links?: { from?: string; to?: string; type?: string; lagDays?: number }[];
}

/** What a read of the model state came to. */
export interface ProgrammeRead {
  /** Null where this structure has no schedule worth a baseline. */
  schedule: SerialisedSchedule | null;
  /** Which saved model it came out of, where the key said. */
  modelId: string | null;
  /** Why there is nothing, in words a screen can print. Null on success. */
  why: string | null;
}

/**
 * Find the schedule inside a bridge's modeller state.
 *
 * The state is a blob store keyed by the modeller's own localStorage names —
 * see src/modeler-host/keys.ts. Model states are `bim_modeler_model_state_<id>`
 * and there can be several: a design model, an as-built, a trial.
 *
 * THE MODEL OF RECORD WINS where one is set and saved here (LC-12): it is the
 * model the department has said the bridge is. Otherwise THE NEWEST KEY, by
 * the model id the key ends in, which is the modeller's `m-<epoch millis>`.
 * Either way the id travels onto the baseline row so a reader can see which.
 */
export function readProgramme(entries: Record<string, string>, ofRecord: string | null = null): ProgrammeRead {
  const keys = Object.keys(entries)
    .filter((k) => k.startsWith('bim_modeler_model_state_'))
    .sort();
  const recorded = ofRecord === null ? null : `bim_modeler_model_state_${ofRecord}`;
  if (recorded !== null && keys.includes(recorded)) {
    keys.splice(keys.indexOf(recorded), 1);
    keys.push(recorded);
  }

  if (keys.length === 0) {
    return {
      schedule: null,
      modelId: null,
      why: 'This structure has no saved model, so there is no programme to take a baseline from.',
    };
  }

  const key = keys[keys.length - 1]!;
  const modelId = key.slice('bim_modeler_model_state_'.length) || null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(entries[key] ?? '');
  } catch {
    return {
      schedule: null,
      modelId,
      why: 'The saved model could not be read. Import its CSiBridge export again.',
    };
  }

  const schedule = (parsed as { schedule?: SerialisedSchedule } | null)?.schedule;
  if (!schedule || !Array.isArray(schedule.activities) || schedule.activities.length === 0) {
    return {
      schedule: null,
      modelId,
      why:
        'This model carries no construction schedule. Import its CSiBridge export again, ' +
        'and take the baseline from that.',
    };
  }

  return { schedule, modelId, why: null };
}

/* --------------------------- the solve --------------------------- */

interface Solved {
  id: string;
  name: string;
  kind: string;
  parentId: string | null;
  isGroup: boolean;
  /** Working-day offsets, finish exclusive. */
  earlyStart: number;
  earlyFinish: number;
  totalFloat: number;
  duration: number;
  notBefore: number | null;
}

/** The links a schedule carries between leaves, of any of the four types (LC-44). */
function leafLinks(schedule: SerialisedSchedule, leaves: ReadonlySet<string>): CpmLink[] {
  return (schedule.links ?? [])
    .filter(
      (l) =>
        typeof l.from === 'string' &&
        typeof l.to === 'string' &&
        leaves.has(l.from) &&
        leaves.has(l.to),
    )
    .map((l) => ({
      from: l.from as string,
      to: l.to as string,
      /* An unknown type is read as the vendored default, FS, which is what the
         modeller's own reader does with one. */
      type: isLinkType(l.type) ? l.type : 'FS',
      lag: typeof l.lagDays === 'number' && Number.isFinite(l.lagDays) ? l.lagDays : 0,
    }));
}

/**
 * Forward and backward passes over a serialised schedule, in working days.
 *
 * The vendored `Recompute()`, extended in the two ways ./cpm.ts states: all
 * four link types, and offsets that are working days on a calendar. What is
 * kept, in its order: leaves only take part; links only between leaves;
 * Kahn's topological sort with cycle nodes appended and solved as though they
 * had no predecessors; a forward pass driven by `max(predecessor + lag,
 * manual start)`; project finish as the maximum leaf finish; a backward pass
 * where an activity with no successor takes the project finish; and groups
 * rolled up afterwards to the span of their children, with zero float.
 *
 * Offsets, not dates. `baselineFrom` turns them into days on the calendar.
 */
export function solveSchedule(schedule: SerialisedSchedule): Solved[] {
  const acts = (schedule.activities ?? []).filter((a) => typeof a.id === 'string' && a.id !== '');

  const byId = new Map(acts.map((a) => [a.id as string, a]));
  const childrenOf = new Map<string, string[]>();
  for (const a of acts) {
    childrenOf.set(a.id as string, (a.children ?? []).filter((c) => byId.has(c)));
  }

  const isLeaf = (id: string): boolean => (childrenOf.get(id) ?? []).length === 0;
  const leafIds = acts.map((a) => a.id as string).filter(isLeaf);
  const leaves = new Set(leafIds);
  const manualOf = (id: string): number | null => {
    const m = byId.get(id)?.manualStartDays;
    return typeof m === 'number' && Number.isFinite(m) ? m : null;
  };

  const nodes: CpmNode[] = leafIds.map((id) => ({
    id,
    duration: Math.max(0, byId.get(id)!.durationDays ?? 0),
    notBefore: manualOf(id),
  }));
  const { times } = solveCpm(nodes, leafLinks(schedule, leaves));

  const solved = new Map<string, Solved>();
  for (const a of acts) {
    const id = a.id as string;
    const t = times.get(id);
    const leaf = isLeaf(id);
    solved.set(id, {
      id,
      name: a.name ?? id,
      kind: a.componentKind ?? '',
      parentId: a.parentId ?? null,
      isGroup: !leaf,
      earlyStart: t?.es ?? 0,
      earlyFinish: t?.ef ?? 0,
      totalFloat: t ? t.ls - t.es : 0,
      duration: leaf ? Math.max(0, a.durationDays ?? 0) : 0,
      notBefore: leaf ? manualOf(id) : null,
    });
  }

  /* Groups roll up to the span of their children and carry no float — they are
     not critical-path candidates. Recursive with a visited set, as the
     vendored version is, so a malformed tree cannot spin. */
  const rolled = new Set<string>();
  const rollUp = (id: string): void => {
    if (rolled.has(id)) return;
    rolled.add(id);
    const kids = childrenOf.get(id) ?? [];
    if (kids.length === 0) return;
    let earliest = Number.POSITIVE_INFINITY;
    let latest = Number.NEGATIVE_INFINITY;
    for (const kid of kids) {
      rollUp(kid);
      const c = solved.get(kid);
      if (!c) continue;
      if (c.earlyStart < earliest) earliest = c.earlyStart;
      if (c.earlyFinish > latest) latest = c.earlyFinish;
    }
    const me = solved.get(id);
    if (!me) return;
    me.earlyStart = Number.isFinite(earliest) ? earliest : 0;
    me.earlyFinish = Number.isFinite(latest) ? latest : 0;
    me.totalFloat = 0;
  };
  for (const id of byId.keys()) rollUp(id);

  return [...solved.values()];
}

/* --------------------------- into calendar days --------------------------- */

/**
 * The frozen baseline, from a serialised schedule, on a working calendar.
 *
 * **THE START IS REQUIRED** (LC-42). It is the contract's commencement date,
 * or the modeller's own project start where no commencement is given. It used
 * to fall back to today, which made every planned date a statement about the
 * day somebody pressed a button; a schedule with no start now throws, and the
 * store says why.
 *
 * **THE FINISH IS THE LAST DAY OF WORK, NOT THE DAY AFTER.** The CPM treats a
 * finish as exclusive — it is the start of the next activity in a chain — so a
 * three-day pour finishes on its third WORKING day, which is the working day
 * before the exclusive offset. A zero-duration activity — a milestone —
 * finishes on the day it starts.
 *
 * Durations and float are working days; the dates are calendar days.
 */
export function baselineFrom(
  schedule: SerialisedSchedule,
  calendar: WorkCalendar = EVERY_DAY,
  start: string | null = schedule.projectStartIso ?? null,
): BaselineActivity[] {
  if (start === null || !isIsoDay(start)) {
    throw new Error(
      'This programme has no start date. Give the contract’s commencement date and take the baseline again.',
    );
  }
  const index = new CalendarIndex(calendar, start);

  return solveSchedule(schedule).map((a) => ({
    activityId: a.id,
    name: a.name,
    kind: a.kind,
    parentId: a.parentId,
    plannedStart: index.dateAt(a.earlyStart),
    plannedFinish: index.dateAt(Math.max(a.earlyStart, a.earlyFinish - 1)),
    floatDays: a.totalFloat,
    isGroup: a.isGroup,
    ...(a.isGroup ? {} : { durationDays: a.duration }),
    ...(a.notBefore === null ? {} : { notBefore: index.dateAt(a.notBefore) }),
  }));
}

/**
 * The links to freeze with a baseline: every one between two leaves, with its
 * type and lag, so the Gantt can draw them and the forecast re-solve with them
 * (LC-44). Before this item they were dropped, and SS, FF and SF with them.
 * One per pair of activities, as the modeller's own `AddLink` keeps them.
 */
export function baselineLinks(schedule: SerialisedSchedule): BaselineLink[] {
  const acts = (schedule.activities ?? []).filter((a) => typeof a.id === 'string' && a.id !== '');
  const ids = new Set(acts.map((a) => a.id as string));
  const leaves = new Set(
    acts.filter((a) => (a.children ?? []).filter((c) => ids.has(c)).length === 0).map((a) => a.id as string),
  );
  const seen = new Set<string>();
  const out: BaselineLink[] = [];
  for (const l of leafLinks(schedule, leaves)) {
    const key = `${l.from}>${l.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ from: l.from, to: l.to, type: l.type, lagDays: l.lag });
  }
  return out;
}

/* --------------------------- proposing a match --------------------------- */

/**
 * The component a scheduled activity most likely refers to, or null.
 *
 * A SUGGESTION AND NEVER A RECORD. The ROADMAP decision of 2026-09-16 reserved
 * the join for a person; this only saves them typing. It returns null far more
 * readily than it guesses, because a wrong suggestion somebody accepts without
 * reading is worse than no suggestion at all.
 *
 * The match is on the qualified component name, which both vocabularies share:
 * the scheduled activity's name comes from the bridge tree, and a progress
 * entry's `component` comes from the same tree through the picker. Exact
 * first, then a single case-insensitive hit — and never where two components
 * would both answer, because choosing between them is the judgement this is
 * not allowed to make.
 */
export function proposeComponent(
  activityName: string,
  components: readonly string[],
): string | null {
  const exact = components.find((c) => c === activityName);
  if (exact) return exact;

  const lowered = activityName.trim().toLowerCase();
  const hits = components.filter((c) => c.trim().toLowerCase() === lowered);
  return hits.length === 1 ? hits[0]! : null;
}

/**
 * What "Match all" proposes for one activity, or null (QA 2026-09-30).
 *
 * An activity that says what it builds — one from an imported model's own parts
 * — proposes exactly that, id and all, with nothing to look up and no 3D view
 * needed. Only the rest fall back to a match on the name.
 */
export function proposeMatch(
  a: Pick<BaselineActivity, 'name' | 'component' | 'componentId'>,
  components: readonly string[],
  idOf: (component: string) => string | null,
): { component: string; componentId: string | null } | null {
  if (a.component) return { component: a.component, componentId: a.componentId ?? null };
  const component = proposeComponent(a.name, components);
  return component === null ? null : { component, componentId: idOf(component) };
}

/**
 * The components a baseline's activities say they build, once each, as a picker
 * offers them (QA 2026-09-30). So a site record can name the component a
 * programmed activity builds without the 3D view having been opened on this
 * device, and a Scheduler can match without it either. Where two share a name —
 * G-1 on every span — the id tells them apart.
 */
export function baselineComponents(
  activities: readonly Pick<BaselineActivity, 'component' | 'componentId' | 'isGroup'>[],
): { name: string; id: string | null; text: string }[] {
  const out: { name: string; id: string | null; text: string }[] = [];
  const seen = new Set<string>();
  for (const a of activities) {
    if (a.isGroup || !a.component) continue;
    const id = a.componentId ?? null;
    const key = id ?? `name:${a.component}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: a.component, id, text: a.component });
  }
  const counts = new Map<string, number>();
  for (const c of out) counts.set(c.name, (counts.get(c.name) ?? 0) + 1);
  for (const c of out) if (c.id !== null && counts.get(c.name)! > 1) c.text = `${c.name} · ${c.id}`;
  return out;
}
